import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

/**
 * Fetches enabled tracking snippets from the backend and injects them into
 * <head> / start-of-<body> / end-of-<body> on every public page (skips /admin).
 *
 * Why this is non-trivial:
 *   - <script> nodes inserted via innerHTML don't execute. We have to
 *     re-create them with document.createElement('script').
 *   - When a snippet contains a `<script src="…">` loader followed by an
 *     inline `<script>` that depends on the loader (gtag config, GTM init,
 *     Writesonic SEO Fixer's wsSEOfixer.configure, etc.), we MUST wait for
 *     the loader's `load` event before appending the inline script.
 *     The HTML `async=false` ordering trick only helps when ALL scripts are
 *     `src=` — inline scripts execute synchronously the moment they're
 *     appended to the DOM, which breaks dependency ordering.
 *
 * Each injected node is tagged with `data-dc-snippet="<id>"` so the same
 * snippet isn't injected twice (StrictMode re-mount, refetch, etc.).
 */
const ATTR = "data-dc-snippet";

function buildScript(rawNode) {
  const s = document.createElement("script");
  for (const { name, value } of Array.from(rawNode.attributes)) {
    s.setAttribute(name, value);
  }
  if (rawNode.textContent) s.text = rawNode.textContent;
  // Force-ordered execution for src= scripts in the same snippet — preserves
  // document order with other async=false src= scripts.
  if (!rawNode.hasAttribute("async")) s.async = false;
  return s;
}

function buildNode(rawNode) {
  if (rawNode.tagName === "SCRIPT") return buildScript(rawNode);
  // For everything else (noscript, meta, img, iframe, link) clone in full —
  // preserves attributes and child nodes verbatim.
  return rawNode.cloneNode(true);
}

function waitForScript(scriptEl) {
  return new Promise((resolve) => {
    const done = () => resolve();
    scriptEl.addEventListener("load", done, { once: true });
    // If the loader 404s we still continue — better to let dependent scripts
    // fail loudly than to stall the rest of the snippet chain.
    scriptEl.addEventListener("error", done, { once: true });
  });
}

async function injectSnippet(snip, target, prepend) {
  if (!snip?.code) return;
  if (document.querySelector(`[${ATTR}="${snip.id}"]`)) return;

  const container = document.createElement("div");
  container.innerHTML = snip.code;
  const rawNodes = Array.from(container.childNodes);

  // Walk children in order. For each <script src="…">, append it and wait for
  // its `load` event before moving on. Inline scripts and non-script nodes
  // append immediately.
  for (const n of rawNodes) {
    if (n.nodeType !== Node.ELEMENT_NODE) continue;
    const built = buildNode(n);
    built.setAttribute(ATTR, snip.id);

    if (prepend && target.firstChild) {
      target.insertBefore(built, target.firstChild);
    } else {
      target.appendChild(built);
    }

    if (n.tagName === "SCRIPT" && n.hasAttribute("src")) {
      // Block until the loader finishes so inline scripts later in the
      // snippet (e.g. wsSEOfixer.configure(...), gtag('config', ...)) find
      // their globals defined.
      // eslint-disable-next-line no-await-in-loop
      await waitForScript(built);
    }
  }
}

async function injectGroup(snippets, target, prepend = false) {
  if (!target || !Array.isArray(snippets)) return;
  for (const snip of snippets) {
    // eslint-disable-next-line no-await-in-loop
    await injectSnippet(snip, target, prepend);
  }
}

export default function TrackingInjector() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios
      .get(`${API}/tracking-snippets`)
      .then(async (res) => {
        if (cancelled) return;
        const data = res?.data || {};
        // Run the three position groups in parallel — they target different
        // parts of the DOM and have no cross-dependencies.
        await Promise.all([
          injectGroup(data.head, document.head),
          injectGroup(data.body_start, document.body, /* prepend */ true),
          injectGroup(data.body_end, document.body),
        ]);
      })
      .catch(() => {
        /* fail silent — tracking is best-effort */
      });
    return () => {
      cancelled = true;
    };
    // Depend only on `isAdmin` — snippets are injected once per page load
    // and persist across SPA navigation. Switching to/from /admin is the
    // only flow that needs to re-run this effect.
  }, [isAdmin]);

  return null;
}
